import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    TextField,
    CircularProgress,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    FormControl,
    Box,
    InputLabel,
    NativeSelect,
} from "@mui/material";
import { useSnackbar } from "notistack";
import http, { buildFormData, ROUTES } from "src/utils/httpHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm, FormProvider, useFormContext } from "react-hook-form";
import * as Yup from "yup";
import i18n from "src/i18n/i18n";
import PageHeader from "src/components/PageHeader";
import Card from "@mui/material/Card";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

export function InvoicerXmlToPdf() {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const formRules = Yup.object().shape({
      xml: Yup.mixed()
        .required(i18n.t("VALIDATION.REQUIRED")),
      note: Yup.string()
        .max(500),
      font: Yup.string()
        .max(50)
        .required(i18n.t("VALIDATION.REQUIRED")),
      font_size: Yup.string()
        .max(30)
        .required(i18n.t("VALIDATION.REQUIRED")),
      combine: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_cfdi_uses: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_obj_imp: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_payment_methods: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_payment_types: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_prod_serv_keys: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_tax_regimes_expeditor: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_tax_regimes_client: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_tax_types: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_tax_types_items: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
      conf_unit_keys: Yup.number()
        .min(0)
        .max(2)
        .required(i18n.t("VALIDATION.REQUIRED")),
    });

    const initialValues = {
      xml: "",
      note: "",
      font: "Arial",
      font_size: "10px",
      font_size_title: "14px",
      combine: "0",
      conf_cfdi_uses: "0",
      conf_obj_imp: "0",
      conf_payment_methods: "0",
      conf_payment_types: "0",
      conf_prod_serv_keys: "0",
      conf_tax_regimes_expeditor: "0",
      conf_tax_regimes_client: "0",
      conf_tax_types: "0",
      conf_tax_types_items: "0",
      conf_unit_keys: "0",
    };

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, setValue, reset } = useForm({
      mode: 'onTouched',
      resolver: yupResolver(formRules),
      defaultValues: initialValues
    });

    function getConfigFromXML(event) {
      let inputFile = (document.getElementById('xml') as HTMLInputElement).files[0];
      if (inputFile) {
        const formData = new FormData();
        formData.set("xml", inputFile);
        http.request({
          method: 'post',
          url: '/api/invoicer/get-config-from-xml',
          data: formData,
        })
        .then((response) => {
          let invoiceClientConfig = response.data.data.config;
          let configValues = {
            note: invoiceClientConfig ? invoiceClientConfig.note : "",
            font: invoiceClientConfig ? invoiceClientConfig.font : "Arial",
            font_size: invoiceClientConfig ? invoiceClientConfig.font_size : "10px",
            font_size_title: invoiceClientConfig ? invoiceClientConfig.font_size_title : "14px",
            combine: invoiceClientConfig ? invoiceClientConfig.combine : "0",
            conf_cfdi_uses: invoiceClientConfig ? invoiceClientConfig.conf_cfdi_uses : "0",
            conf_obj_imp: invoiceClientConfig ? invoiceClientConfig.conf_obj_imp : "0",
            conf_payment_methods: invoiceClientConfig ? invoiceClientConfig.conf_payment_methods : "0",
            conf_payment_types: invoiceClientConfig ? invoiceClientConfig.conf_payment_types : "0",
            conf_prod_serv_keys: invoiceClientConfig ? invoiceClientConfig.conf_prod_serv_keys : "0",
            conf_tax_regimes_expeditor: invoiceClientConfig ? invoiceClientConfig.conf_tax_regimes_expeditor : "0",
            conf_tax_regimes_client: invoiceClientConfig ? invoiceClientConfig.conf_tax_regimes_client : "0",
            conf_tax_types: invoiceClientConfig ? invoiceClientConfig.conf_tax_types : "0",
            conf_tax_types_items: invoiceClientConfig ? invoiceClientConfig.conf_tax_types_items : "0",
            conf_unit_keys: invoiceClientConfig ? invoiceClientConfig.conf_unit_keys : "0",
          };
          reset(configValues);
          (document.getElementById('select-combine') as HTMLInputElement).value = configValues.combine;
        })
        .catch((error) => {
          console.error(error);
          enqueueSnackbar(error.message, { variant: 'error' });
        });
      }
    }

    return (
      <Fragment>
        <PageHeader
          heading={t(("INVOICER-XML-TO-PDF.HEADING").toUpperCase())}
          subheading={t(("INVOICER-XML-TO-PDF.SUBHEADING").toUpperCase())}
          Icon={PictureAsPdfIcon}
        />

        <Grid
          sx={{
            px: 4,
            pb: 4
          }}
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={4}
        >
          <Grid 
            sx={{
              pl: 4,
              pt: 4,
            }}
          >
            <Card>
              <Box p={2}>
                <Grid container display={'flex'} justifyContent={'space-between'}>
                  <form id="myForm" noValidate onSubmit={handleSubmit(async (values) => {
                    const formData = new FormData();
                    buildFormData(formData, {...values});
                    let inputFile = (document.getElementById('xml') as HTMLInputElement).files[0];
                    if (inputFile) {
                      formData.set("xml", inputFile);
                    } else {
                      formData.delete("xml");
                    }
                    await http.request({
                      method: 'post',
                      url: '/api/invoicer/get-pdf-from-xml',
                      data: formData,
                      responseType: "blob",
                    })
                    .then((response) => {
                      //Create a Blob from the PDF Stream
                      const file = new Blob([response.data], { type: "application/pdf" });
                      //Build a URL from the file
                      const fileURL = URL.createObjectURL(file);
                      //Open the URL on new Window
                      const pdfWindow = window.open();
                      pdfWindow.location.href = fileURL;
                      enqueueSnackbar(response.data.message, { variant: 'success' });
                    })
                    .catch(async (error) => {
                      const errorResponse = JSON.parse(await error.text());
                      console.error(errorResponse);
                      enqueueSnackbar(errorResponse.message, { variant: 'error', autoHideDuration: 18000 });
                    });
                  })}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12}>
                        <InputLabel htmlFor="xml">XML</InputLabel>
                        <Controller
                          name="xml"
                          control={control}
                          render={({ field }) => (
                            <input
                              accept="text/xml"
                              id="xml"
                              type="file"
                              onChange={(event) =>{
                                getConfigFromXML(event);
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <Controller
                          name="note"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              error={Boolean(errors.note)}
                              fullWidth
                              label={t("FIELDS.NOTE")}
                              variant="outlined"
                              {...field}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <Controller
                          name="font"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              error={Boolean(errors.font)}
                              fullWidth
                              label={t("FIELDS.FONT")}
                              variant="outlined"
                              {...field}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <Controller
                          name="font_size"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              error={Boolean(errors.font_size)}
                              fullWidth
                              label={t("FIELDS.FONT_SIZE")}
                              variant="outlined"
                              {...field}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <Controller
                          name="font_size_title"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              error={Boolean(errors.font_size_title)}
                              fullWidth
                              label={t("FIELDS.FONT_SIZE_TITLE")}
                              variant="outlined"
                              {...field}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12} mt={2}>
                        <InputLabel variant="standard" htmlFor="select-combine">
                          {t("FIELDS.COMBINE_ITEMS")}
                        </InputLabel>
                        <NativeSelect
                          fullWidth
                          inputProps={{
                            name: 'combine',
                            id: 'select-combine',
                          }}
                          onChange={(event) => {
                            setValue('combine', event.target.value);
                          }}
                          >
                            <option value={0}>No combinar</option>
                            <option value={1}>Combinar por clave del SAT (ClaveProdServ)</option>
                            <option value={2}>Combinar por clave del SAP (NoIdentificacion)</option>
                        </NativeSelect>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} 
                      sx={{
                        display: 'flex', 
                        justifyContent: 'space-between',
                        pt: 3,
                        pl: 1
                      }}
                    >
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_CFDI_USES")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_cfdi_uses"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_OBJ_IMP")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_obj_imp"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_PAYMENT_METHODS")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_payment_methods"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_PAYMENT_TYPES")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_payment_types"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_PROD_SERV_KEYS")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_prod_serv_keys"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_TAX_REGIMES_EXPEDITOR")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_tax_regimes_expeditor"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_TAX_REGIMES_CLIENT")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_tax_regimes_client"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_TAX_TYPES")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_tax_types"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_TAX_TYPES_ITEMS")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_tax_types_items"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">{t("FIELDS.CONF_UNIT_KEYS")}</FormLabel>
                          <Controller
                            rules={{ required: true }}
                            control={control}
                            name="conf_unit_keys"
                            render={({ field }) => (
                              <RadioGroup {...field}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                  <FormControlLabel
                                    value="0"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_0")}
                                  />
                                  <FormControlLabel
                                    value="1"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_1")}
                                  />
                                  <FormControlLabel
                                    value="2"
                                    control={<Radio />}
                                    label={t("FIELDS.CONF_VALUE_2")}
                                  />
                                </Box>
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12} display="flex" justifyContent="flex-end">
                        <Button
                          sx={{
                            ml: 2
                          }}
                          type="submit"
                          startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                          }
                          disabled={(() => {
                            return isSubmitting || !isValid
                        })()}
                          variant="contained"
                        >
                          {t("INVOICER-XML-TO-PDF.CREATE")}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Fragment>
    )
}